/**
 * Location and working-hours overlap helpers.
 *
 * Geography matters to this product for exactly one reason: whether an expert is
 * awake while the customer's incident is live. Continent and country are only
 * useful as a way to guess a timezone when we were not told one.
 *
 * The previous implementation derived both parties' timezones from
 * `Intl.DateTimeFormat().resolvedOptions()`, which returns whoever is looking at
 * the screen. Both sides therefore always came back identical, the "same
 * timezone" branch always won, and the score collapsed into a continent
 * comparison wearing a timezone label.
 */

/** The viewer's own timezone. Correct for stamping your own profile, only. */
export const detectTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.warn("Could not detect timezone:", error);
    return "UTC";
  }
};

const countryToContinent = {
  // Europe
  Germany: "Europe", France: "Europe", Italy: "Europe", Spain: "Europe",
  "United Kingdom": "Europe", Netherlands: "Europe", Belgium: "Europe",
  Switzerland: "Europe", Austria: "Europe", Sweden: "Europe", Norway: "Europe",
  Denmark: "Europe", Finland: "Europe", Poland: "Europe", Portugal: "Europe",
  Greece: "Europe", Ireland: "Europe", "Czech Republic": "Europe",
  Romania: "Europe", Hungary: "Europe", Ukraine: "Europe", Russia: "Europe",

  // North America
  "United States": "North America", USA: "North America", US: "North America",
  Canada: "North America", Mexico: "North America",

  // South America
  Brazil: "South America", Argentina: "South America", Chile: "South America",
  Colombia: "South America", Peru: "South America",

  // Asia
  China: "Asia", Japan: "Asia", India: "Asia", "South Korea": "Asia",
  Singapore: "Asia", Malaysia: "Asia", Thailand: "Asia", Indonesia: "Asia",
  Philippines: "Asia", Vietnam: "Asia", Pakistan: "Asia", Bangladesh: "Asia",
  Turkey: "Asia", "Saudi Arabia": "Asia", "United Arab Emirates": "Asia",
  UAE: "Asia", Israel: "Asia",

  // Africa
  "South Africa": "Africa", Egypt: "Africa", Nigeria: "Africa", Kenya: "Africa",
  Morocco: "Africa", Ghana: "Africa",

  // Oceania
  Australia: "Oceania", "New Zealand": "Oceania",
};

/**
 * One representative IANA zone per country, used to estimate working hours when
 * a profile gives us a place but not a timezone. Countries spanning several
 * zones resolve to their largest business centre, which is close enough for
 * "can these two be online together" and wrong by hours for nobody who matters.
 */
const countryToTimezone = {
  Germany: "Europe/Berlin", France: "Europe/Paris", Italy: "Europe/Rome",
  Spain: "Europe/Madrid", "United Kingdom": "Europe/London",
  Netherlands: "Europe/Amsterdam", Belgium: "Europe/Brussels",
  Switzerland: "Europe/Zurich", Austria: "Europe/Vienna",
  Sweden: "Europe/Stockholm", Norway: "Europe/Oslo",
  Denmark: "Europe/Copenhagen", Finland: "Europe/Helsinki",
  Poland: "Europe/Warsaw", Portugal: "Europe/Lisbon", Greece: "Europe/Athens",
  Ireland: "Europe/Dublin", "Czech Republic": "Europe/Prague",
  Romania: "Europe/Bucharest", Hungary: "Europe/Budapest",
  Ukraine: "Europe/Kyiv", Russia: "Europe/Moscow",

  "United States": "America/New_York", USA: "America/New_York",
  US: "America/New_York", Canada: "America/Toronto",
  Mexico: "America/Mexico_City",

  Brazil: "America/Sao_Paulo",
  Argentina: "America/Argentina/Buenos_Aires", Chile: "America/Santiago",
  Colombia: "America/Bogota", Peru: "America/Lima",

  China: "Asia/Shanghai", Japan: "Asia/Tokyo", India: "Asia/Kolkata",
  "South Korea": "Asia/Seoul", Singapore: "Asia/Singapore",
  Malaysia: "Asia/Kuala_Lumpur", Thailand: "Asia/Bangkok",
  Indonesia: "Asia/Jakarta", Philippines: "Asia/Manila",
  Vietnam: "Asia/Ho_Chi_Minh", Pakistan: "Asia/Karachi",
  Bangladesh: "Asia/Dhaka", Turkey: "Europe/Istanbul",
  "Saudi Arabia": "Asia/Riyadh", "United Arab Emirates": "Asia/Dubai",
  UAE: "Asia/Dubai", Israel: "Asia/Jerusalem",

  "South Africa": "Africa/Johannesburg", Egypt: "Africa/Cairo",
  Nigeria: "Africa/Lagos", Kenya: "Africa/Nairobi",
  Morocco: "Africa/Casablanca", Ghana: "Africa/Accra",

  Australia: "Australia/Sydney", "New Zealand": "Pacific/Auckland",
};

/**
 * Parses "Berlin, Germany" into structured form.
 *
 * Returns `timezone: null` when the place is unknown, so callers can tell "we
 * do not know" apart from "we know, and it is UTC".
 */
export const parseLocation = (locationString) => {
  if (!locationString || !String(locationString).trim()) {
    return { city: "", country: "", continent: "", timezone: null };
  }

  const parts = String(locationString).split(",").map((p) => p.trim());
  const city = parts[0] || "";
  const country = parts[1] || parts[0] || "";

  return {
    city,
    country,
    continent: countryToContinent[country] || "",
    timezone: countryToTimezone[country] || null,
  };
};

/** Normalises either a location string or an already-structured object. */
function toLocation(value) {
  if (!value) return null;
  if (typeof value === "string") return parseLocation(value);

  // A stored object may carry a country but no timezone, or vice versa.
  return {
    city: value.city || "",
    country: value.country || "",
    continent: value.continent || countryToContinent[value.country] || "",
    timezone: value.timezone || countryToTimezone[value.country] || null,
  };
}

/**
 * A zone's UTC offset in minutes right now, honouring daylight saving.
 *
 * Formats the same instant into the target zone and reads it back as if it were
 * UTC; the difference is the offset. This is the standard trick and avoids
 * shipping a timezone database.
 */
function offsetMinutes(timeZone, date = new Date()) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
      .formatToParts(date)
      .reduce((acc, part) => {
        acc[part.type] = part.value;
        return acc;
      }, {});

    const asUTC = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour) % 24,
      Number(parts.minute),
      Number(parts.second)
    );

    // Drop milliseconds from the reference instant so the two agree.
    return (asUTC - Math.floor(date.getTime() / 1000) * 1000) / 60000;
  } catch (error) {
    console.warn(`Unknown timezone "${timeZone}":`, error.message);
    return null;
  }
}

/** Whole hours between two zones, or null if either is unknown. Capped at 12. */
export const getTimezoneDifference = (timezone1, timezone2) => {
  if (!timezone1 || !timezone2) return null;

  const first = offsetMinutes(timezone1);
  const second = offsetMinutes(timezone2);
  if (first === null || second === null) return null;

  let hours = Math.abs(first - second) / 60;
  // 23 hours apart is really 1 hour apart in the other direction.
  if (hours > 12) hours = 24 - hours;
  return Math.round(hours * 10) / 10;
};

/**
 * How well two parties' working days overlap.
 *
 * @returns {{score: number, hoursApart: number|null, label: string, known: boolean}}
 *   `score` is 0-1. When we cannot tell, it is a neutral 0.5 rather than a
 *   penalty — an expert should not sink in the ranking because a customer left
 *   their location blank.
 */
export const evaluateLocationFit = (clientLocation, helperLocation) => {
  const client = toLocation(clientLocation);
  const helper = toLocation(helperLocation);
  const hoursApart = getTimezoneDifference(client?.timezone, helper?.timezone);

  if (hoursApart === null) {
    return {
      score: 0.5,
      hoursApart: null,
      label: "Working hours unknown",
      known: false,
    };
  }

  if (hoursApart <= 1) {
    return { score: 1, hoursApart, label: "Same working hours", known: true };
  }
  if (hoursApart <= 3) {
    return {
      score: 0.9,
      hoursApart,
      label: `Within ${hoursApart}h of you`,
      known: true,
    };
  }
  if (hoursApart <= 6) {
    return {
      score: 0.65,
      hoursApart,
      label: `${hoursApart}h offset — mornings overlap`,
      known: true,
    };
  }
  if (hoursApart <= 9) {
    return {
      score: 0.35,
      hoursApart,
      label: `${hoursApart}h offset — little overlap`,
      known: true,
    };
  }
  return {
    score: 0.15,
    hoursApart,
    label: `${hoursApart}h offset — opposite hours`,
    known: true,
  };
};
